import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import {
	dataRemovalRequestDataStatus,
	dataRemovalRequestIndexQueryKey,
	roleEnum,
	useDataRemovalRequestApprove,
	useDataRemovalRequestIndex,
	useDataRemovalRequestReject,
	useOrganisationPrivacyShow,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmationDialog, ConfirmationDialogRef, TextField } from '../../../rapid-ui'
import { SettingsSubheading } from '../shared'

function StatusBadge({ status }: { status: string }) {
	const { t } = useTranslation('common')

	const styles: Record<string, string> = {
		[dataRemovalRequestDataStatus.notified]: 'bg-amber-100 text-amber-800',
		[dataRemovalRequestDataStatus.approved]: 'bg-blue-100 text-blue-800',
		[dataRemovalRequestDataStatus.completed]: 'bg-green-100 text-green-800',
		[dataRemovalRequestDataStatus.rejected]: 'bg-red-100 text-red-800',
		[dataRemovalRequestDataStatus.pending]: 'bg-grey-100 text-grey-600',
		[dataRemovalRequestDataStatus.expired]: 'bg-grey-100 text-grey-600',
	}

	return (
		<span
			className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
				styles[status] ?? 'bg-grey-100 text-grey-600'
			}`}
		>
			{t(`settings.dataRemovalRequests.status.${status}`)}
		</span>
	)
}

function formatDate(dateString: string): string {
	return new Date(dateString).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	})
}

/**
 * Self-contained: fetches and mutates its own data via TanStack Query rather
 * than receiving a viewModel built by the route. See the
 * jobilla-frontend-data-fetching skill for why this is the house pattern now.
 */
export function DataRemovalRequests() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const { role } = useAuth()
	const canManagePrivacy = role === roleEnum.admin || role === roleEnum.jobillan
	const queryClient = useQueryClient()

	const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null)
	const [rejectionReason, setRejectionReason] = useState('')
	const approveConfirmationRef = useRef<ConfirmationDialogRef>(null)

	const privacyQuery = useOrganisationPrivacyShow(jobillaOrgId, {
		enabled: !!jobillaOrgId && canManagePrivacy,
	})

	const autoApprove = privacyQuery.data?.autoApproveDataRemovalRequests ?? true

	const dataRemovalRequestsQuery = useDataRemovalRequestIndex(jobillaOrgId, {
		enabled: !!jobillaOrgId && canManagePrivacy && !autoApprove,
	})

	const invalidateRequests = () =>
		void queryClient.invalidateQueries({ queryKey: dataRemovalRequestIndexQueryKey(jobillaOrgId) })

	const approveMutation = useDataRemovalRequestApprove({ onSuccess: invalidateRequests })
	const rejectMutation = useDataRemovalRequestReject({
		onSuccess: () => {
			setRejectingRequestId(null)
			setRejectionReason('')
			invalidateRequests()
		},
	})

	const handleApprove = async (requestId: string) => {
		try {
			await approveConfirmationRef.current?.confirm({
				title: t('settings.dataRemovalRequests.approveConfirmTitle'),
				message: t('settings.dataRemovalRequests.approveConfirmMessage'),
				confirmText: t('settings.dataRemovalRequests.approve'),
				cancelText: t('common.cancel'),
				destructive: true,
			})
			approveMutation.mutate({ organisation: jobillaOrgId, dataRemovalRequest: requestId })
		} catch {
			// User cancelled
		}
	}

	if (autoApprove) {
		return null
	}

	const dpoEmail = privacyQuery.data?.dataProtectionOfficer?.emailAddress

	if (dataRemovalRequestsQuery.isPending) {
		return (
			<section className="space-y-6">
				<SettingsSubheading>{t('settings.dataRemovalRequests.title')}</SettingsSubheading>
				<p>{t('common.loading')}</p>
			</section>
		)
	}

	const requests = dataRemovalRequestsQuery.data?.data ?? []

	return (
		<section className="space-y-6">
			<ConfirmationDialog ref={approveConfirmationRef} />
			<SettingsSubheading>{t('settings.dataRemovalRequests.title')}</SettingsSubheading>
			<p>{t('settings.dataRemovalRequests.description')}</p>

			{!dpoEmail && (
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
					{t('settings.dataRemovalRequests.noDpoWarning')}
				</div>
			)}

			{dataRemovalRequestsQuery.isError && (
				<p className="text-sm text-red-600">{t('settings.dataRemovalRequests.loadError')}</p>
			)}

			{requests.length === 0
				? <p className="text-grey-500 text-sm">{t('settings.dataRemovalRequests.empty')}</p>
				: (
					<div className="space-y-3">
						{requests.map((request) => {
							const isActionable = request.status === dataRemovalRequestDataStatus.notified
							const isApproving = approveMutation.isPending
								&& approveMutation.variables?.dataRemovalRequest === request.id
							const isRejecting = rejectMutation.isPending
								&& rejectMutation.variables?.dataRemovalRequest === request.id
							const isRejectFormOpen = rejectingRequestId === request.id
							const approveFailed = approveMutation.isError
								&& approveMutation.variables?.dataRemovalRequest === request.id

							return (
								<div key={request.id} className="rounded-md border border-grey-200 p-4 space-y-3">
									<div className="flex justify-between items-start">
										<div className="space-y-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium">
													{t('settings.dataRemovalRequests.requestedOn', {
														date: formatDate(request.requestedAt),
													})}
												</span>
												<StatusBadge status={request.status} />
											</div>
											<p className="text-xs text-grey-500">
												{t('settings.dataRemovalRequests.candidateRef', {
													id: request.candidateId.slice(0, 8),
												})}
											</p>
											{request.status === dataRemovalRequestDataStatus.rejected
												&& request.rejectionReason && (
												<p className="text-xs text-grey-500">
													{t('settings.dataRemovalRequests.rejectedReason', {
														reason: request.rejectionReason,
													})}
												</p>
											)}
											{request.completedAt && (
												<p className="text-xs text-grey-500">
													{t('settings.dataRemovalRequests.completedOn', {
														date: formatDate(request.completedAt),
													})}
												</p>
											)}
											{request.decidedAt
												&& request.status !== dataRemovalRequestDataStatus.completed && (
												<p className="text-xs text-grey-500">
													{t('settings.dataRemovalRequests.decidedOn', {
														date: formatDate(request.decidedAt),
													})}
												</p>
											)}
										</div>
										{isActionable && !isRejectFormOpen && (
											<div className="flex gap-2">
												<Button
													type="committal"
													size="short"
													onClick={() => handleApprove(request.id)}
													isLoading={isApproving}
													disabled={isApproving || isRejecting}
												>
													{t('settings.dataRemovalRequests.approve')}
												</Button>
												<Button
													type="destructive"
													size="short"
													onClick={() => {
														setRejectingRequestId(request.id)
														setRejectionReason('')
														rejectMutation.reset()
													}}
													disabled={isApproving || isRejecting}
												>
													{t('settings.dataRemovalRequests.reject')}
												</Button>
											</div>
										)}
									</div>

									{isRejectFormOpen && (
										<div className="border-t border-grey-200 pt-3 space-y-3">
											<TextField
												label={t('settings.dataRemovalRequests.rejectionReasonLabel')}
												value={rejectionReason}
												onChange={setRejectionReason}
												placeholder={t(
													'settings.dataRemovalRequests.rejectionReasonPlaceholder',
												)}
											/>
											{rejectMutation.isError && (
												<p className="text-sm text-red-600">{rejectMutation.error?.message}</p>
											)}
											<div className="flex gap-2">
												<Button
													type="destructive"
													size="short"
													onClick={() => {
														const reason = rejectionReason.trim()
														if (!reason) { return }
														rejectMutation.mutate({
															organisation: jobillaOrgId,
															dataRemovalRequest: request.id,
															data: { rejectionReason: reason },
														})
													}}
													isLoading={isRejecting}
													disabled={!rejectionReason.trim() || isRejecting}
												>
													{t('settings.dataRemovalRequests.confirmReject')}
												</Button>
												<Button
													type="subtle"
													size="short"
													onClick={() => {
														setRejectingRequestId(null)
														setRejectionReason('')
													}}
													disabled={isRejecting}
												>
													{t('common.cancel')}
												</Button>
											</div>
										</div>
									)}

									{approveFailed && (
										<p className="text-sm text-red-600">{approveMutation.error?.message}</p>
									)}
								</div>
							)
						})}
					</div>
				)}
		</section>
	)
}
